<template>
	<form
		class="grid grid-cols-1 gap-4 rounded bg-gray-50/30 p-4 sm:grid-cols-2"
		@submit.prevent="submit"
	>
		<v-page-product-rating-form-control
			class="sm:col-span-2"
			:label="$t('reviews.what-would-you-rate')"
			no-label
		>
			<h-radio-group
				v-model="validate.rating.$model"
				class="group mr-auto flex items-center rounded text-4xl text-gray-100 hover:text-black"
				:class="{
					'ring-1 ring-red-500': validate.rating.$error,
				}"
			>
				<!-- eslint-disable-next-line vuejs-accessibility/mouse-events-have-key-events -->
				<h-radio-group-option
					v-for="i in 5"
					:key="i"
					:value="i"
					as="button"
					type="button"
					class="peer px-px transition-colors duration-75 peer-hover:text-gray-100 active:text-gray-700"
					:class="{
						'text-black': i <= review.rating,
					}"
				>
					<v-icon icon="star" />
				</h-radio-group-option>
			</h-radio-group>
		</v-page-product-rating-form-control>

		<v-page-product-rating-form-control
			class="flex flex-col space-y-1 sm:col-span-2"
			:label="$t('reviews.tell-us-feedback')"
		>
			<v-input
				v-model="validate.review.$model"
				:error="validate.review.$error"
				type="textarea"
			/>
		</v-page-product-rating-form-control>

		<v-page-product-rating-form-control
			:label="$t('reviews.your-name')"
			:note="$t('reviews.your-name-note')"
		>
			<v-input
				v-model="validate.name.$model"
				:error="validate.name.$error"
				type="text"
			/>
		</v-page-product-rating-form-control>

		<v-page-product-rating-form-control
			:label="$t('reviews.your-email')"
			:note="$t('reviews.your-email-note')"
		>
			<v-input
				v-model="validate.email.$model"
				:error="validate.email.$error"
				type="email"
			/>
		</v-page-product-rating-form-control>

		<div
			class="mt-4 grid grid-cols-2 justify-end space-x-4 sm:col-span-2 sm:flex"
		>
			<v-button theme="gray" type="button" @click="$emit('close')">
				{{ $t("reviews.cancel") }}
			</v-button>

			<v-button theme="black" type="submit" :disabled="isLoading">
				{{ $t("reviews.submit") }}
			</v-button>
		</div>
	</form>
</template>

<script lang="ts" setup>
import {useVuelidate} from "@vuelidate/core";
import {required, email, minValue} from "@vuelidate/validators";
import {useToast} from "@pkg/shared/store/toast";

const properties = defineProps<{
	sku: string;
}>();

const emit = defineEmits<{
	(event: "close"): void;
}>();

const {t} = useI18n({
	useScope: "global",
});

const isLoading = ref(false);

const review = reactive({
	rating: 0,
	review: "",
	name: "",
	email: "",
});

const validate = useVuelidate(
	{
		rating: {required, minValue: minValue(1)},
		review: {required},
		name: {required},
		email: {required, email},
	},
	review,
);

const toast = useToast();
const {csrf} = useCsrf();

async function submit() {
	await validate.value.$validate();

	if (validate.value.$error) {
		return;
	}

	isLoading.value = true;

	await $fetch("/api/reviews/reviews", {
		method: "POST",
		body: {
			sku: properties.sku,
			rating: review.rating,
			review: review.review,
			name: review.name,
			email: review.email,
		},
		headers: {
			...(csrf && {
				"csrf-token": csrf,
			}),
		},
	});

	isLoading.value = false;

	toast.add("success", t("reviews.review-sent"));

	emit("close");

	// review.rating = 0;
	// review.review = '';
	// review.name = '';
	// review.email = '';
}
</script>
